/**
 * Product Video Modal — Floating play button opens fullscreen video overlay.
 * Supports YouTube, Vimeo, and Shopify-hosted video files.
 * Extends ModalDialog (global.js) for scroll lock, focus trap, ESC close.
 */
(function () {
  'use strict';
  if (window.__ProductVideoModalInitialized) return;
  window.__ProductVideoModalInitialized = true;

  /* ── URL Parsers ─────────────────────────────────────────── */
  function parseYouTubeId(url) {
    if (!url) return null;
    var m;
    m = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
    m = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
    m = url.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
    m = url.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
    return null;
  }

  function isYouTubeShorts(url) {
    return url && /\/shorts\//.test(url);
  }

  function parseVimeoId(url) {
    if (!url) return null;
    var m = url.match(/vimeo\.com\/(\d+)/);
    return m ? m[1] : null;
  }

  /* ── Custom Element ──────────────────────────────────────── */
  if (!customElements.get('product-video-modal')) {
    customElements.define('product-video-modal', class extends ModalDialog {
      constructor() {
        super();
        this._videoWrapper = this.querySelector('.pvm-video-wrapper');
      }

      show(opener) {
        this.openedBy = opener;

        var type = opener.dataset.videoType;
        var videoUrl = opener.dataset.videoUrl || '';
        var fileUrl = opener.dataset.videoFileUrl || '';

        this._clearVideo();

        if (type === 'hosted' && fileUrl) {
          this._loadHostedDeferred(fileUrl, opener);
          return;
        }

        if (videoUrl) {
          var ytId = parseYouTubeId(videoUrl);
          var vimeoId = parseVimeoId(videoUrl);
          if (ytId) {
            this._setRatio(isYouTubeShorts(videoUrl) ? 9 / 16 : 16 / 9);
            this._loadYouTube(ytId);
          } else if (vimeoId) {
            this._setRatio(16 / 9);
            this._loadVimeo(vimeoId);
          }
        }

        super.show(opener);
      }

      hide() {
        this.classList.add('pvm-closing');
        var self = this;
        setTimeout(function () {
          self.classList.remove('pvm-closing');
          self._clearVideo();
          ModalDialog.prototype.hide.call(self);
        }, 250);
      }

      _loadYouTube(id) {
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + id
          + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.setAttribute('loading', 'lazy');
        this._videoWrapper.appendChild(iframe);
      }

      _loadVimeo(id) {
        var iframe = document.createElement('iframe');
        iframe.src = 'https://player.vimeo.com/video/' + id
          + '?autoplay=1&playsinline=1';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        this._videoWrapper.appendChild(iframe);
      }

      _loadHostedDeferred(url, opener) {
        var self = this;
        var container = this.querySelector('.pvm-container');
        var overlay = this.querySelector('.pvm-overlay');
        var revealed = false;
        var showTime = Date.now();

        // 1. Hide the video container, show overlay + spinner immediately
        container.classList.add('pvm-loading');

        var spinner = document.createElement('div');
        spinner.className = 'pvm-spinner';
        overlay.appendChild(spinner);

        // Show modal (overlay + spinner visible, container hidden)
        ModalDialog.prototype.show.call(this, opener);

        // 2. When metadata loads, reveal the correctly-sized container
        function reveal() {
          if (revealed) return;
          revealed = true;
          clearTimeout(fallback);

          // Ensure spinner shows for at least 300ms so it doesn't flash
          var elapsed = Date.now() - showTime;
          var delay = Math.max(0, 300 - elapsed);

          setTimeout(function () {
            // Compute actual aspect ratio from the video's intrinsic dimensions
            var vw = video.videoWidth || 1;
            var vh = video.videoHeight || 1;
            var ratio = vw / vh;
            self._videoWrapper.style.setProperty('--pvm-r', ratio.toFixed(4));

            video.controls = true;
            video.autoplay = true;

            // Remove spinner
            if (spinner.parentNode) spinner.parentNode.removeChild(spinner);

            // Unhide container, kill CSS animation, let WAAPI handle reveal
            container.classList.remove('pvm-loading');
            container.style.cssText = 'animation:none;';

            // Force layout so container settles at correct size before animating
            void container.offsetHeight;

            container.animate([
              { opacity: 0, transform: 'translateY(24px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ], {
              duration: 300,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              fill: 'forwards'
            });

            video.play().catch(function () {});
          }, delay);
        }

        var video = document.createElement('video');
        video.preload = 'auto';
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.src = url;

        video.addEventListener('loadedmetadata', function () {
          reveal();
        });

        // Append into DOM so iOS Safari will fetch metadata
        this._videoWrapper.appendChild(video);

        // Fallback if metadata is very slow
        var fallback = setTimeout(function () {
          reveal();
        }, 4000);
      }

      _setRatio(ratio) {
        if (!this._videoWrapper) return;
        this._videoWrapper.style.setProperty('--pvm-r', ratio.toFixed(4));
      }

      _clearVideo() {
        if (!this._videoWrapper) return;
        this._videoWrapper.querySelectorAll('video').forEach(function (v) { v.pause(); });
        this._videoWrapper.innerHTML = '';
        this._videoWrapper.style.setProperty('--pvm-r', '1.7778');

        // Clean up loading state
        var container = this.querySelector('.pvm-container');
        if (container) {
          container.classList.remove('pvm-loading');
          container.style.cssText = '';
        }
        var spinner = this.querySelector('.pvm-spinner');
        if (spinner) spinner.parentNode.removeChild(spinner);
      }
    });
  }

  /* ── Event Delegation ────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.product-video-trigger');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    var modal = document.querySelector('product-video-modal');
    if (modal) modal.show(trigger);
  });

  /* ── Render Lucide play icon ─────────────────────────────── */
  if (window.lucide) {
    window.lucide.createIcons();
  }
})();
